import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * @typedef {{
 *   tabs: Element[],
 *   buttons: Element[],
 *   panels: Element[],
 *   images: Element[]
 * }} Refs
 */

/** @extends {Component<Refs>} */
export class AdPicWithAccordionComponent extends Component {
    #activeIndex = 0;
    #abortController = new AbortController();

    /** @type {ReturnType<typeof setInterval> | null} */
    #autoplayTimer = null;

    connectedCallback() {
        super.connectedCallback();
        if (!this.refs.tabs?.length) return;
        this.#activate(0);
        this.#initMode();
        this.#reserveSetup();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.#stopAutoplay();
    }

    updatedCallback() {
        if (!this.refs.tabs?.length) return;
        this.#stopAutoplay();
        this.#abortController.abort();
        this.#abortController = new AbortController();
        this.#activate(0);
        this.#initMode();
        this.#reserveSetup();
    }

    // ── Declarative event handlers ──────────────────────────────

    /** @param {MouseEvent} event */
    handleTabClick(event) {
        const button = /** @type {Element} */ (event.target).closest('.na-pic-with-accordion__tab-trigger');
        if (!button) return;
        const index = this.refs.buttons.indexOf(button);
        if (index !== -1) this.#activate(index);
    }

    /** @param {KeyboardEvent} event */
    handleKeydown(event) {
        const { key } = event;
        const count = this.refs.buttons?.length ?? 0;
        if (count === 0) return;

        const activeEl = this.contains(document.activeElement) ? document.activeElement : null;
        const currentIndex = activeEl ? this.refs.buttons.indexOf(/** @type {Element} */ (activeEl)) : -1;
        const base = currentIndex !== -1 ? currentIndex : this.#activeIndex;

        let next = -1;
        if (key === 'ArrowDown') next = (base + 1) % count;
        else if (key === 'ArrowUp') next = (base - 1 + count) % count;
        else if (key === 'Home') next = 0;
        else if (key === 'End') next = count - 1;
        else return;

        event.preventDefault();
        this.#activate(next);
        /** @type {HTMLElement | undefined} */ (this.refs.buttons[next])?.focus();
    }

    // ── Private ─────────────────────────────────────────────────

    /** @param {number} index */
    #activate(index) {
        this.#activeIndex = index;

        this.refs.tabs?.forEach((tab, i) => {
            tab.classList.toggle('is-active', i === index);
        });

        this.refs.buttons?.forEach((button, i) => {
            const isActive = i === index;
            button.setAttribute('aria-selected', String(isActive));
            button.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        if (!this.dataset.fixedImage) {
            this.refs.images?.forEach((image, i) => {
                image.classList.toggle('is-active', i === index);
            });
        }
    }

    #initMode() {
        const mode = this.dataset.interactionMode ?? 'manual';
        if (mode === 'scroll') this.#initScroll();
        else if (mode === 'autoplay') this.#initAutoplay();
    }

    #initScroll() {
        const { signal } = this.#abortController;
        const count = this.refs.tabs?.length ?? 0;
        if (count === 0) return;

        const update = () => {
            const rect = this.getBoundingClientRect();
            const vh = window.innerHeight;

            // Divide the section's full viewport crossing evenly across all tabs.
            // progress=0 when section enters from bottom, progress=1 when it exits from top.
            // Works on any viewport/section size without magic constants.
            const progress = (vh - rect.top) / (vh + rect.height);
            const index = Math.floor(Math.max(0, Math.min(0.999, progress)) * count);

            this.#activate(index);
        };

        window.addEventListener('scroll', update, { signal, passive: true });
        update();
    }

    #initAutoplay() {
        const seconds = parseInt(this.dataset.autoplayInterval ?? '4', 10) || 4;
        const interval = seconds * 1000;

        this.#startAutoplay(interval);

        const { signal } = this.#abortController;
        this.addEventListener('mouseenter', () => this.#stopAutoplay(), { signal });
        this.addEventListener('mouseleave', () => this.#startAutoplay(interval), { signal });
        this.addEventListener('focusin', () => this.#stopAutoplay(), { signal });
        this.addEventListener('focusout', () => this.#startAutoplay(interval), { signal });
    }

    /** @param {number} interval */
    #startAutoplay(interval) {
        this.#stopAutoplay();
        const count = this.refs.tabs?.length ?? 0;
        if (count === 0) return;
        this.#autoplayTimer = setInterval(() => {
            this.#activate((this.#activeIndex + 1) % count);
        }, interval);
    }

    #stopAutoplay() {
        if (this.#autoplayTimer !== null) {
            clearInterval(this.#autoplayTimer);
            this.#autoplayTimer = null;
        }
    }

    #reserveSetup() {
        this.#reservePanelHeight();
        document.fonts?.ready?.then(() => this.#reservePanelHeight());
        window.addEventListener('resize', this.#handleReserveResize, {
            signal: this.#abortController.signal,
            passive: true,
        });
    }

    #handleReserveResize = debounce(() => this.#reservePanelHeight(), 150);

    /**
     * Reserve a constant height on the content column equal to its tallest-panel
     * state, so switching tabs never changes the layout height (no jump / image
     * re-centering). Each tab's real rendered height is measured synchronously
     * with transitions off (no visible flicker); the max is exposed as
     * `--content-min-height`.
     */
    #reservePanelHeight = () => {
        const content = this.querySelector('.na-pic-with-accordion__content');
        const tabs = this.refs.tabs;
        if (!(content instanceof HTMLElement) || !tabs?.length) return;

        let activeIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
        if (activeIndex === -1) activeIndex = 0;

        this.style.removeProperty('--content-min-height');
        this.dataset.measuring = '';

        let maxHeight = 0;
        for (let i = 0; i < tabs.length; i++) {
            tabs.forEach((tab, j) => tab.classList.toggle('is-active', i === j));
            maxHeight = Math.max(maxHeight, content.getBoundingClientRect().height);
        }

        tabs.forEach((tab, j) => tab.classList.toggle('is-active', j === activeIndex));
        delete this.dataset.measuring;

        if (maxHeight > 0) this.style.setProperty('--content-min-height', `${Math.round(maxHeight)}px`);
    };
}

if (!customElements.get('na-pic-with-accordion-component')) {
    customElements.define('na-pic-with-accordion-component', AdPicWithAccordionComponent);
}
